import { Alert, Spinner } from '@acpaas-ui/react-components';
import React, { useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { logBackIn } from '../../actions/auth.actions';
import { fetchReset } from '../../actions/fetch.actions';
import { resourceReset, resourceUpdate } from '../../actions/resources.actions';
import { HTTP_UNAUTHORIZED } from '../../constants/http.constants';
import { DOSSIERS_PATH } from '../../constants/paths.constants';
import { useFormsEngineHook } from '../../hooks/useFormsEngine.hook';
import { FORMS_ENGINE_FORM_RESOURCE } from '../../resources/forms.engine.form.resource';
import { forms } from '../../services/bff.api';

const Widget = window.auiEmbeddableWidgets.reactComponent(
  'https://formwidget.antwerpen.be/definitions/v1/form-renderer.json',
  {
    React,
    ReactDOM
  }
);

const FormsEngineWidget = ({
  className = '',
  slug,
  submitExtraData = {},
  extraData = {},
  submitAttachments = [],
  prefillData = {},
  prefillConfig,
  onAfterSubmit,
  onBeforeSubmit,
  onValidate
}) => {
  const dispatch = useDispatch();
  const formWrapperRef = useRef(null);

  const { fetchError, fetching, fetched, data, jsonData } = useFormsEngineHook(slug, { prefillConfig, prefillData });
  const [submitDone, setSubmitDone] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(
    async (content) => {
      try {
        const result = await forms.submit({
          form: JSON.stringify({ content, definitionId: jsonData?.id, slug }),
          extraData: JSON.stringify(submitExtraData),
          attachments: submitAttachments
        });
        if (result.status !== 200) {
          throw result.status;
        }
      } catch (error) {
        if (error === HTTP_UNAUTHORIZED) {
          localStorage.setItem('recoverForm', 'slug');
          return logBackIn();
        }
        dispatch(resourceReset(FORMS_ENGINE_FORM_RESOURCE));
        dispatch(fetchReset(FORMS_ENGINE_FORM_RESOURCE));
        setSubmitting(false);
        setSubmitError(true);
        return;
      }

      dispatch(resourceUpdate(FORMS_ENGINE_FORM_RESOURCE, 'data', content));
      setSubmitting(false);
      setSubmitDone(true);

      if (onAfterSubmit) {
        onAfterSubmit();
      }
    },
    [dispatch, jsonData?.id, onAfterSubmit, slug, submitExtraData, submitAttachments]
  );

  const startSubmit = useCallback(
    async (data) => {
      setSubmitting(true);

      formWrapperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const content = { ...data, ...extraData };
      if (onBeforeSubmit) {
        onBeforeSubmit(
          content,
          () => submit(content),
          () => {
            setSubmitting(false);
            setSubmitError(true);
          }
        );
      } else {
        submit(content);
      }
    },
    [extraData, onBeforeSubmit, submit]
  );

  const handleSubmit = useCallback(
    (data) => {
      if (onValidate) {
        onValidate(
          { ...data, ...extraData },
          () => startSubmit(data),
          () => {
            console.log('validate error');
          }
        );
      } else {
        startSubmit(data);
      }
    },
    [extraData, onValidate, startSubmit]
  );

  const renderContent = () => {
    if (submitting) {
      return (
        <>
          <h4 className="u-margin-bottom">{'Aangifte wordt verstuurd...'}</h4>
          <Spinner />
        </>
      );
    }

    if (submitDone) {
      return (
        <Alert type="success">
          <p>Jouw aanvraag is succesvol ingediend. We gaan hiermee aan de slag.</p>
          <p>
            De status van jouw aanvraag kan je steeds raadplegen in het overzicht{' '}
            <Link to={DOSSIERS_PATH}>Dossiers</Link>.
          </p>
        </Alert>
      );
    }

    if (submitError) {
      return <Alert type="danger">{'Aangifte indienen mislukt.'}</Alert>;
    }

    return <Widget schema={jsonData?.template?.content || {}} form={data || {}} onSubmit={handleSubmit} />;
  };

  if (fetchError) {
    return (
      <div className="u-padding">
        <Alert type="danger">Kan formulier niet laden.</Alert>
      </div>
    );
  }

  if (fetching || !fetched) {
    return (
      <div className="u-padding u-text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div
      ref={formWrapperRef}
      className={`spo-o-forms u-padding ${className} ${submitDone ? 'spo-o-forms--submitted' : ''} ${
        submitError ? 'spo-o-forms--error' : ''
      } ${submitting ? 'spo-o-forms--loading' : ''}`}
    >
      {renderContent()}
    </div>
  );
};

export default FormsEngineWidget;
